import { useEffect, useRef, useState } from 'react';
import { SparklesIcon, CheckIcon } from '@heroicons/react/24/solid';
import './tryout.css';

// ============================================================================
// TryoutGeneratingStage — mirrors the internal "Leeren + Prüfen" step:
// the original room sits beside the result-in-progress, and the loading
// overlay (scan sweep, rotating phase, progress) lives ON the result image
// so nothing pushes the layout taller. Keeps the visitor engaged for the
// generation (empty or staging) without scrolling.
//
// While waiting, USP bubbles pop up over the generating image and a heading
// above invites the visitor to see what estage.ai can do. A parent-enforced
// minimum display time (see MIN_GENERATING_MS in TryoutView) guarantees the
// visitor has time to read them even when a baked preset resolves instantly.
// ============================================================================

interface TryoutGeneratingStageProps {
  imageUrl: string;
  aspectRatio: '1:1' | '4:3';
  /** Left frame image; defaults to imageUrl (the room being transformed). */
  originalUrl?: string;
  /** Rotating status lines shown on the result frame. */
  phases?: string[];
  /** Corner tags for the two frames. */
  originalLabel?: string;
  resultLabel?: string;
  /** ETA pill shown next to the status line. */
  etaLabel?: string;
  /** When true, plays the completion animation (bar → 100%, check + "Fertig!")
   *  just before the parent reveals the result. */
  finished?: boolean;
}

// Grounded in what the model does: read the room, remove the furniture,
// protect the shell.
const EMPTY_PHASES = [
  'Raum wird analysiert…',
  'Möbel & Objekte werden erkannt…',
  'Einrichtung wird entfernt…',
  'Wände, Boden & Fenster bleiben erhalten…',
  'Letzte Details werden geglättet…',
];

// USPs taken from the landing page (FeaturesSection) + the result-step feature list.
const USPS: { icon: string; text: string }[] = [
  { icon: '🧹', text: 'Räume digital leerräumen' },
  { icon: '🛋️', text: 'Virtuelles Staging in vielen Stilen' },
  { icon: '📐', text: '3D-Grundrisse aus 2D-Plänen' },
  { icon: '📸', text: 'Fotos direkt vom Mieter anfordern' },
  { icon: '🔍', text: 'KI-Upscaling für Print-Qualität' },
  { icon: '🏷️', text: 'Eigenes Branding & Overlay' },
  { icon: '📄', text: 'Exposé in Minuten erstellen' },
  { icon: '💶', text: 'Höhere Verkaufspreise erzielen' },
];

// Anchor points on the result frame — kept clear of the top-right result tag
// and the bottom progress card. Each slot cycles through the USP list.
const BUBBLE_SLOTS: { pos: React.CSSProperties; delay: number }[] = [
  { pos: { left: '6%',  top: '14%' }, delay: 0 },
  { pos: { right: '6%', top: '38%' }, delay: 0.12 },
  { pos: { left: '9%',  top: '60%' }, delay: 0.24 },
];

const SPARKLES = [
  { left: 32, top: 60, delay: 0 },
  { left: 66, top: 68, delay: 0.6 },
  { left: 50, top: 44, delay: 1.2 },
];

// The frame hugs the image: the <img> is a normal block element (w-full,
// h-auto) that defines the frame's height, and every effect/overlay is
// absolutely positioned on top. This guarantees the image fills the frame with
// no bottom gap regardless of the photo's aspect ratio. Both frames render the
// same source image, so they stay equal height side by side.
const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative w-full overflow-hidden rounded-[16px] border border-[#EBEBE6] bg-[#F5F4F0]">
    {children}
  </div>
);

const CornerTag: React.FC<{ side: 'left' | 'right'; dark?: boolean; children: React.ReactNode }> = ({
  side, dark, children,
}) => (
  <div
    className={`absolute top-2.5 ${side === 'left' ? 'left-2.5' : 'right-2.5'} z-[4] text-[11px] font-semibold px-2.5 py-1 rounded-full ${
      dark ? 'bg-[#001116]/85 text-white' : 'bg-white/95 text-[#001116] border border-[#EBEBE6]'
    }`}
  >
    {children}
  </div>
);

export const TryoutGeneratingStage: React.FC<TryoutGeneratingStageProps> = ({
  imageUrl,
  originalUrl,
  phases = EMPTY_PHASES,
  originalLabel = 'Original',
  resultLabel = 'Leergeräumt',
  etaLabel = '~1 Min',
  finished = false,
}) => {
  const [phase, setPhase] = useState(0);
  const [phaseVisible, setPhaseVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  // Advances the USP bubbles: each tick swaps every slot to the next feature.
  const [bubbleTick, setBubbleTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setPhaseVisible(false);
      setTimeout(() => {
        setPhase((i) => Math.min(i + 1, phases.length - 1));
        setPhaseVisible(true);
      }, 260);
    }, 4200);
    return () => clearInterval(id);
  }, [phases.length]);

  useEffect(() => {
    const id = setInterval(() => setBubbleTick((t) => t + 1), 4200);
    return () => clearInterval(id);
  }, []);

  // Ease toward ~94% over ~32s, then hold. Updated every animation frame and
  // rendered with no CSS width transition, so the fill is continuous rather than
  // stepping between transition targets.
  const startRef = useRef<number | null>(null);
  useEffect(() => {
    let frame: number;
    const DURATION = 32000;
    const CAP = 94;
    const tick = (t: number) => {
      if (startRef.current === null) startRef.current = t;
      const linear = Math.min(1, (t - startRef.current) / DURATION);
      setProgress((1 - Math.pow(1 - linear, 1.8)) * CAP);
      if (linear < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const leftUrl = originalUrl ?? imageUrl;
  const displayProgress = finished ? 100 : progress;

  return (
    <div style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
      <p className="tryout-usp-heading">
        Während du auf das Ergebnis wartest, schau dir an, was <strong>estage.ai</strong> alles kann:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
        {/* Original */}
        <Frame>
          <img src={leftUrl} alt={originalLabel} className="block w-full h-auto" draggable={false} />
          <CornerTag side="left">{originalLabel}</CornerTag>
        </Frame>

        {/* Result being generated */}
        <Frame>
          <img
            src={imageUrl}
            alt={resultLabel}
            className="tryout-scan-img block w-full h-auto"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />

          {/* Scan sweep + sparkles + bubbles run while generating; on finish
              they clear so the completion state reads cleanly. */}
          {!finished && (
            <>
              {/* Scan sweep */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="tryout-scan-sweep absolute left-0 right-0" style={{ top: 0, height: '40%' }}>
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, rgba(254,111,23,0) 0%, rgba(254,111,23,0.12) 70%, rgba(254,111,23,0.22) 100%)' }}
                  />
                  <div
                    className="absolute left-0 right-0 bottom-0"
                    style={{
                      height: 2,
                      background: 'linear-gradient(90deg, transparent, #FFF3EC 30%, #FE6F17 50%, #FFF3EC 70%, transparent)',
                      boxShadow: '0 0 14px 2px rgba(254,111,23,0.65)',
                    }}
                  />
                </div>
              </div>

              {/* Sparkles */}
              {SPARKLES.map((s, i) => (
                <div
                  key={i}
                  className="tryout-sparkle absolute pointer-events-none"
                  style={{ left: `${s.left}%`, top: `${s.top}%`, animationDelay: `${s.delay}s` }}
                >
                  <SparklesIcon className="w-4 h-4" style={{ color: '#FFFFFF', filter: 'drop-shadow(0 0 6px rgba(254,111,23,0.9))' }} />
                </div>
              ))}

              {/* USP bubbles — each slot pops a fresh feature every tick */}
              {BUBBLE_SLOTS.map((slot, i) => {
                const usp = USPS[(bubbleTick * BUBBLE_SLOTS.length + i) % USPS.length];
                return (
                  <div key={`${i}-${bubbleTick}`} className="tryout-usp-bubble-wrap" style={slot.pos}>
                    <div className="tryout-usp-bubble" style={{ animationDelay: `${slot.delay}s` }}>
                      <span className="tryout-usp-emoji">{usp.icon}</span>
                      {usp.text}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Completion flourish — a check badge pops in just before reveal. */}
          {finished && (
            <div className="absolute inset-0 z-[5] flex items-center justify-center bg-black/25 backdrop-blur-[1px]">
              <div className="tryout-finish-pop flex flex-col items-center gap-2.5">
                <div className="w-16 h-16 rounded-full bg-[#FE6F17] flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(254,111,23,0.7)]">
                  <CheckIcon className="w-9 h-9 text-white" strokeWidth={2.5} />
                </div>
                <span className="text-white font-semibold text-[15px] [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]">
                  Fertig!
                </span>
              </div>
            </div>
          )}

          <CornerTag side="right" dark>{resultLabel}</CornerTag>

          {/* Status + progress flush to the bottom edge — no floating card, so
              the image fills the frame with no bottom gap. The bar spans the
              full width for a clear, honest progress read. */}
          <div className="absolute inset-x-0 bottom-0 z-[4]">
            <div className="flex items-center gap-2 px-3.5 pt-8 pb-2.5 bg-gradient-to-t from-black/65 via-black/25 to-transparent">
              <span className={`${finished ? '' : 'tryout-dot'} w-2 h-2 rounded-full bg-[#FE6F17] flex-shrink-0`} />
              <span
                className="text-[13px] font-semibold text-white transition-opacity duration-200 truncate [text-shadow:0_1px_3px_rgba(0,0,0,0.5)]"
                style={{ opacity: finished || phaseVisible ? 1 : 0 }}
              >
                {finished ? 'Fertig!' : phases[phase]}
              </span>
              {!finished && (
                <span className="ml-auto text-[12px] font-semibold text-white/90 flex-shrink-0 [text-shadow:0_1px_3px_rgba(0,0,0,0.5)]">
                  {etaLabel}
                </span>
              )}
            </div>
            <div className="h-2.5 w-full bg-black/30 overflow-hidden relative">
              <div
                className="h-full bg-[#FE6F17]"
                style={{ width: `${displayProgress}%`, transition: finished ? 'width 0.45s ease-out' : undefined }}
              />
              {!finished && (
                <div
                  className="tryout-progress-glint absolute top-0 bottom-0 w-1/3"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent)' }}
                />
              )}
            </div>
          </div>
        </Frame>
      </div>
    </div>
  );
};
